import React from 'react';
import {
  Box, List, InputAdornment, CircularProgress, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { SearchField } from '../../../components/ui';
import useInterveneSx from '../hooks/useInterveneSx';
import InterveneSearchableSelect from './InterveneSearchableSelect';
import InterveneOrganisationSearchableSelect from './InterveneOrganisationSearchableSelect';
import { getChannelLabel, isSameChannel } from '../utils/channelSelectUtils';
import { getOrganisationLabel } from '../utils/organisationSelectUtils';
import FilterTabs from './FilterTabs';
import MessageSoundToggle from './MessageSoundToggle';
import UserListItem from './UserListItem';
import EmptyState from '../../../components/ui/EmptyState';
import { INTERVENE_TAB_CONFIG } from '../constants/interveneApi';
import { getUserListBadgeMode } from '../utils/userListBadgeRules';

export default function UserList({
  users,
  chatCounts,
  filters,
  isLoading,
  activeUser,
  onTabChange,
  onSearch,
  onScroll,
  onSelectUser,
  channels,
  activeChannel,
  onChannelSelect,
  isLoadingChannels,
  isSuperAdmin,
  activeOrganisation,
  onOrganisationSelect,
  hasSelectedOrganisation,
  hasActiveChannel,
}) {
  const interveneSx = useInterveneSx();
  const styles = interveneSx.userList;

  const orgLabel = getOrganisationLabel(activeOrganisation);
  const channelLabel = getChannelLabel(activeChannel);

  const showOrgTooltip = Boolean(orgLabel && orgLabel.length > 8);
  const showChannelTooltip = Boolean(channelLabel && channelLabel.length > 8);

  const showConversationControls = hasActiveChannel;
  const showChannelField = hasSelectedOrganisation;

  const showSelectorRow = isSuperAdmin || showChannelField;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.controls}>
        {showSelectorRow && (
          <Box
            sx={[
              styles.selectorGrid,
              isSuperAdmin && showChannelField && styles.selectorGridTwoCol,
            ]}
          >
            {isSuperAdmin && (
              <Box sx={styles.selectorCell}>
                <Tooltip
                  title={showOrgTooltip ? orgLabel : ''}
                  placement="top"
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [{ name: 'offset', options: { offset: [0, -15] } }],
                    },
                    tooltip: { sx: { fontSize: '0.75rem', maxWidth: 300 } },
                  }}
                >
                  <Box sx={styles.compactFieldWrap}>
                    <InterveneOrganisationSearchableSelect
                      label="Organisation"
                      valueId={filters.organisationId || null}
                      selectedOrganisation={activeOrganisation}
                      onChange={onOrganisationSelect}
                      placeholder="Select organisation"
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessOutlinedIcon sx={styles.selectorIcon} />
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}

            {showChannelField && (
              <Box sx={styles.selectorCell}>
                <Tooltip
                  title={showChannelTooltip ? channelLabel : ''}
                  placement="top"
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [{ name: 'offset', options: { offset: [0, -15] } }],
                    },
                    tooltip: { sx: { fontSize: '0.75rem', maxWidth: 300 } },
                  }}
                >
                  <Box sx={styles.compactFieldWrap}>
                    <InterveneSearchableSelect
                      variant="channel"
                      label="Bot channel"
                      options={channels || []}
                      value={activeChannel}
                      onChange={onChannelSelect}
                      getOptionLabel={getChannelLabel}
                      isOptionEqualToValue={isSameChannel}
                      loading={isLoadingChannels}
                      clearable={false}
                      disabled={!hasSelectedOrganisation}
                      placeholder="Select channel"
                      noOptionsText={
                        isLoadingChannels
                          ? 'Loading channels...'
                          : 'No bot channels found for this organisation'
                      }
                      startAdornment={
                        <InputAdornment position="start">
                          <StorefrontOutlinedIcon sx={styles.selectorIcon} />
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {showConversationControls && (
          <>
            <Box sx={styles.searchStatusRow}>
              <SearchField
                placeholder="Search conversations..."
                value={filters.search || ''}
                // onChange={(event) => onSearch(event.target.value)}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/^\s+/, '')
                    .replace(/\s{2,}/g, ' ');

                  onSearch(value);
                }}
                size="sm"
                icon={<SearchIcon fontSize="small" />}
                sx={styles.conversationSearch}
              />
              <MessageSoundToggle compact />
            </Box>

            <FilterTabs
              value={filters.userType}
              onChange={onTabChange}
              tabs={INTERVENE_TAB_CONFIG}
              chatCounts={chatCounts}
            />
          </>
        )}
      </Box>

      <Box sx={styles.scrollArea} onScroll={onScroll}>
        {!showConversationControls ? (
          <EmptyState
            title={
              isSuperAdmin && !hasSelectedOrganisation
                ? 'Select an organisation'
                : 'Select a bot channel'
            }
            description={
              isSuperAdmin && !hasSelectedOrganisation
                ? 'Choose an organisation to load WhatsApp bot channels and conversations.'
                : 'Pick a bot channel to view and manage customer conversations.'
            }
            sx={styles.emptyState}
          />
        ) : isLoading && users.length === 0 ? (
          <Box sx={styles.loadingCenter}>
            <CircularProgress size={28} sx={styles.spinner} />
          </Box>
        ) : users.length === 0 ? (
          <EmptyState
            title="No conversations"
            description="No conversations in this tab yet."
            sx={styles.emptyState}
          />
        ) : (
          <List disablePadding sx={styles.list}>
            {users.map((user) => {
              const isSelected = activeUser?.phone_number === user.phone_number;
              const rowKey =
                user.phone_number ||
                user.last_message?.phone_number ||
                `${user.whatsapp_name || 'user'}-${user.updated_at || ''}`;

              return (
                <UserListItem
                  key={rowKey}
                  user={user}
                  isSelected={isSelected}
                  badgeMode={getUserListBadgeMode(filters.userType)}
                  tabValue={filters.userType}
                  onSelect={onSelectUser}
                />
              );
            })}
          </List>
        )}

        {showConversationControls && isLoading && users.length > 0 && (
          <Box sx={styles.loadingMore}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
